import React from "react";
import { Grid as MuiGrid } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Visibility } from "@mui/icons-material";

import { formatItemHtmlList, formatPenalty } from "@framework/exchange";
import { ListAction } from "@framework/styled";
import { Card, List, Table, Typography } from "@dzambalaorg/ui-kit";
import { useGlobalDialogViewCallbacks } from "@framework/shared";

import { normalizeDuration } from "../../../../../../utils/time";
import { StakingDepositButton } from "../../../../../../components/buttons";
import { MarketDialogKeys } from "../../../../../../shared";
import { IStakingRuleExt } from "../view";
import { StyledImage } from "../styled";

interface IStakingRulesItemProps {
  rule: IStakingRuleExt;
}

export const StakingRulesItem = ({ rule }: IStakingRulesItemProps) => {
  const { formatMessage } = useIntl();

  const { openViewDialog } = useGlobalDialogViewCallbacks<IStakingRuleExt>();

  const deposit = formatItemHtmlList(rule.deposit);
  const reward = formatItemHtmlList(rule.reward);

  const onViewToken = (rule: IStakingRuleExt) => () => {
    openViewDialog(MarketDialogKeys.STAKING_RULE_VIEW, rule, {
      message: "dialogs.view",
    });
  };

  return (
    <MuiGrid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card
        header={
          <Card.Header
            title={<StyledImage component="img" src={rule.imageUrl || ""} />}
            subheader={
              <Typography variant="h6" color="textPrimary" textAlign="center">
                {rule.title}
              </Typography>
            }
          />
        }
      >
        <Card.Content>
          <MuiGrid container spacing={2}>
            <MuiGrid size={{ xs: 12, sm: rule.reward ? 6 : 12 }}>
              <Typography fontWeight={500} mb={1}>
                {`${formatMessage({ id: "form.labels.deposit" })}:`}
              </Typography>
              <List>{deposit}</List>
            </MuiGrid>
            {rule.reward && (
              <MuiGrid size={{ xs: 12, sm: 6 }}>
                <Typography fontWeight={500} mb={1}>{`${formatMessage({ id: "form.labels.reward" })}:`}</Typography>
                <List>{reward}</List>
              </MuiGrid>
            )}
          </MuiGrid>
          <Table.Container sx={theme => ({ marginTop: theme.spacing(theme.tokens.spacing.sm) })}>
            <Table aria-label="staking rules table">
              <Table.Body>
                <Table.Row>
                  <Table.Cell component="th" scope="row">
                    <FormattedMessage id="form.labels.durationAmount" />
                  </Table.Cell>
                  <Table.Cell align="right">
                    {formatMessage(
                      { id: `enums.durationUnit.${rule.durationUnit}` },
                      {
                        count: normalizeDuration({
                          durationAmount: rule.durationAmount,
                          durationUnit: rule.durationUnit,
                        }),
                      },
                    )}
                  </Table.Cell>
                </Table.Row>
                {rule.penalty ? (
                  <Table.Row>
                    <Table.Cell component="th" scope="row">
                      <FormattedMessage id="form.labels.penalty" />
                    </Table.Cell>
                    <Table.Cell align="right">{formatPenalty(rule.penalty)}%</Table.Cell>
                  </Table.Row>
                ) : null}
                {rule.stakes.length > 0 ? (
                  <Table.Row>
                    <Table.Cell component="th" scope="row">
                      <FormattedMessage id="form.labels.stakesCount" />
                    </Table.Cell>
                    <Table.Cell align="right">{rule.stakes.length}</Table.Cell>
                  </Table.Row>
                ) : null}
              </Table.Body>
            </Table>
          </Table.Container>
        </Card.Content>
        <Card.Footer>
          <StakingDepositButton rule={rule} />
          <ListAction onClick={onViewToken(rule)} message="form.tips.view" icon={Visibility} />
        </Card.Footer>
      </Card>
    </MuiGrid>
  );
};
